import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { NewspaperIcon } from '@heroicons/react/24/outline';
import ContentRenderer from './admin/ContentRenderer';
import ContactButton from '../components/ContactButton';
const MAX_CONTENT_LENGTH = 200; // The maximum number of characters for the summary
const topicIdMapping = {
  realestate: 'Cq3auS1VsmtbG10Kw2ef',
  execution: 'niwjxuloe5cfnBVAIxPr',
  damages: 'vzOfLHY5Y0lGDXpQbnie',
  powerOfAttorney: 'kuje0EhTG705PyObVt0g'
};

// Function to create a clean summary
const createSummary = (content) => {
  if (!content) return '';
  const strippedContent = content.replace(/<[^>]+>/g, '');
  if (strippedContent.length <= MAX_CONTENT_LENGTH) return strippedContent;
  return strippedContent.substring(0, MAX_CONTENT_LENGTH) + '...';
};

// A new component for rendering an article card
const ArticleCard = ({ title, content, articleId, image, topicId }) => (
  <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 flex flex-col w-full max-w-md mx-auto">
    {/* Article Image */}
    <div className="w-full h-48 flex-shrink-0">
      <img
        src={image || "https://placehold.co/400x300/E5E7EB/1F2937?text=Article+Image"}
        alt={title}
        className="w-full h-full object-cover"
        loading="lazy"
      />
    </div>
         
    {/* Article Content */}
    <div className="flex flex-col flex-grow p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
        <ContentRenderer content={title} />
      </h3>
             
      <p className="text-gray-600 mb-4 line-clamp-3 flex-grow">
        {createSummary(content)}
      </p>
       
      <Link 
        to={`/topic/${topicId}/${articleId}`}
        className="w-full text-center py-3 px-4 rounded-lg text-white font-bold transition-colors mt-auto
                   bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800"
      >
        קרא עוד
      </Link>
    </div>
  </div>
);

const TopicPage = () => {
  const { topicId } = useParams();
  const [topicData, setTopicData] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const isPowerOfAttorney = topicId === 'powerOfAttorney';

  const getTopicImage = (topicData) => {
    if (!topicData || !topicData.name) return '/defaultTopicImage.jpg';
    const name = topicData.name.toLowerCase();
    if (name.includes('נדל')) return '/realEstateTopicImage.jpg';
    if (name.includes('חדלות')) return '/חן3.jpg';
    if (name.includes('נזיקין')) return '/damagesTopicImage.jpg';
    if (name.includes('ייפוי')) return '/חן1.jpg';
    return '/defaultTopicImage.jpg';
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [topicId]);

  useEffect(() => {
    const fetchTopicData = async () => {
      try {
        const mappedTopicId = topicIdMapping[topicId];
        if (!mappedTopicId) {
          setTopicData(null);
          setLoading(false);
          return;
        }

        const topicDoc = await getDoc(doc(db, 'subjects', mappedTopicId));
        if (topicDoc.exists()) {
          const data = topicDoc.data();
          setTopicData(data);

          const articlesQuery = query(
            collection(db, 'articles'),
            where('subjectId', '==', mappedTopicId)
          );
          const articlesSnapshot = await getDocs(articlesQuery);
          setArticles(
            articlesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
          );
        } else {
          setTopicData(null);
        }
      } catch (error) {
        console.error('Error fetching topic data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTopicData();
  }, [topicId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!topicData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 text-gray-800">
        <div className="text-center p-8 bg-white rounded-lg">
          <h2 className="text-2xl font-bold text-blue-700">הנושא לא נמצא</h2>
          <p className="text-gray-600 mt-2">נא לבדוק את הקישור ולנסות שוב</p>
        </div>
      </div>
    );
  }

return (
    <div className="flex-grow bg-gray-100 text-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* 1. Top Section - Image & Articles */}
        <div className="lg:grid lg:grid-cols-1 items-start">
          <div className="relative mb-8 lg:mb-0">
            <div className="relative w-full aspect-video md:aspect-w-16 md:aspect-h-9 overflow-hidden rounded-xl shadow-2xl">
              <img
                src={getTopicImage(topicData)}
                alt={topicData.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-8">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white text-shadow-lg">
                  {topicData.name}
                </h1>
              </div>
            </div>
            {/* Main Content Section moved here */}
            <div className="mt-8">
              <ContentRenderer content={topicData.generalContent} />
            </div>
          </div>

          {/* TODO: מאמרים בנושא — מושבת זמנית לפי בקשת הלקוח
          {!isPowerOfAttorney && articles.length > 0 && (
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold mb-4 text-blue-700 border-b border-gray-300 pb-2">מאמרים בנושא</h2>
              <div className="flex lg:flex-col overflow-x-auto gap-4 pb-4 hide-scrollbar snap-x snap-mandatory">
                {articles.map(article => (
                  <div key={article.id} className="w-[300px] flex-shrink-0 snap-center lg:w-full">
                    <ArticleCard
                      title={article.title}
                      content={article.content}
                      articleId={article.id}
                      image={article.imageUrl}
                      topicId={topicId}
                    />
                  </div>
                ))}
              </div>
              <Link
                to="/contact"
                className="mt-6 w-full hidden lg:block text-center py-4 rounded-lg bg-gray-600 text-white font-bold hover:bg-blue-700 transition-colors"
              >
                צור קשר
              </Link>
            </div>
          )}
          */}
      
        </div>
             <ContactButton />
      </div>
    </div>
  );
};

export default TopicPage;
